import { Link } from "react-router";
import { motion } from "framer-motion";
import { Clock, Heart, Sparkles, Tag } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp } from "@/lib/animations";
import { difficultyLabel, formatDuration, type GuideCard as GuideCardType } from "@/lib/guides";

type Props = {
  guide: GuideCardType;
  index?: number;
  showTopic?: boolean;
};

export default function GuideCard({ guide, index = 0, showTopic = true }: Props) {
  const theme = usePalette();
  const duration = formatDuration(guide.durationMins);
  const difficulty = difficultyLabel(guide.difficulty);

  return (
    <motion.article
      variants={fadeUp}
      custom={index}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
    >
      <Link
        to={`/guides/${guide.topic.slug}/${guide.slug}`}
        className="group block h-full overflow-hidden rounded-3xl transition-shadow hover:shadow-xl"
        style={{
          backgroundColor: colors.cream,
          border: `1px solid ${colors.tan}1f`,
        }}
      >
        <div
          className="relative aspect-[4/3] w-full overflow-hidden"
          style={
            guide.heroImageUrl
              ? undefined
              : {
                  background: `linear-gradient(135deg, ${theme.blob1}, ${theme.warm}, ${theme.blob3})`,
                }
          }
        >
          {guide.heroImageUrl ? (
            <img
              src={guide.heroImageUrl}
              alt={guide.title}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-white">
              <Sparkles size={36} />
            </div>
          )}
          {guide.featured && (
            <span
              className="absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em]"
              style={{
                backgroundColor: colors.cream,
                color: theme.accent,
                fontFamily: fonts.body,
                fontWeight: 700,
              }}
            >
              ★ Featured
            </span>
          )}
        </div>
        <div className="p-5">
          {showTopic && (
            <p
              className="mb-2 text-[11px] uppercase tracking-[0.16em]"
              style={{ fontFamily: fonts.body, fontWeight: 700, color: theme.accent }}
            >
              {guide.topic.name}
            </p>
          )}
          <h3
            className="mb-1 text-2xl leading-tight transition-colors group-hover:opacity-90"
            style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy }}
          >
            {guide.title}
          </h3>
          {guide.subtitle && (
            <p
              className="mb-3 text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: `${colors.navy}99`,
                letterSpacing: "0.02em",
              }}
            >
              {guide.subtitle}
            </p>
          )}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.12em]"
            style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}88` }}
          >
            {duration && (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> {duration}
              </span>
            )}
            {difficulty && (
              <span className="inline-flex items-center gap-1">
                <Sparkles size={12} /> {difficulty}
              </span>
            )}
            {guide.productCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Tag size={12} /> {guide.productCount} {guide.productCount === 1 ? "pick" : "picks"}
              </span>
            )}
            {guide.likeCount > 0 && (
              <span className="inline-flex items-center gap-1" style={{ color: theme.accent }}>
                <Heart size={12} fill="currentColor" /> {guide.likeCount}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
